/**
 * Lender API Service
 *
 * Secure API client for fetching member data through the VeraScore Platform API.
 * Uses cookie-based session authentication (set by /verascore/login).
 *
 * API Endpoints (proxied via /api/verascore/*):
 * - GET /lender/members - List all members shared with lender's org
 * - GET /lender/members/:memberId - Get specific member detail
 *
 * Auth Flow:
 * 1. User logs in via LoginApi (/verascore/login)
 * 2. Backend sets session cookie
 * 3. All API calls include credentials: 'include' to send cookies
 */

import { applyDemo2PersonaOverlay, applyDemo2PersonaOverlayList } from './demo2PersonaOverlay';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;

// PRIV-20.3: Purpose header for PII/NPPI access audit trail
// Header name must be 'x-request-purpose' to match backend purposeValidationMiddleware
const PURPOSE_HEADER = 'x-request-purpose';

/**
 * PRIV-20.3: Purpose types for lender actions
 * - lender_review: Default browsing, no specific intent
 * - underwriting: Actively evaluating a loan application
 * - credit_check: Accessing credit report or credit-related data
 * - fraud_prevention: Investigating suspicious activity
 */
export type LenderPurpose = 'lender_review' | 'underwriting' | 'credit_check' | 'fraud_prevention';

// Default purpose for general member data access
const DEFAULT_PURPOSE: LenderPurpose = 'lender_review';

// ============================================================================
// Types (matching API response from lenderService.ts)
// ============================================================================

export interface LenderMember {
  // Member identification
  member_id: string;
  auth0_user_id: string;
  org_id: string;

  // PII from Auth0
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zipcode?: string;

  // Consent info
  consent_status: string;
  consent_granted_at: string;
  consent_scopes?: string[];

  // VeraScore data
  verascore: number;
  verascore_rating: string;
  score_month: string;

  // Factor scores (0-100)
  factor_dti: number;
  factor_liquid_reserves: number;
  factor_savings: number;
  factor_cash_flow: number;
  factor_debt: number;
  factor_payment_history: number;

  // Income
  yearly_gross_income: number;
  monthly_gross_income: number;

  // BUG-UI-062: Income basis metadata for transparency
  // Which income source was used for scoring
  income_basis_used?: 'ATTESTED' | 'DEPOSITED_NET' | 'VERIFIED_GROSS' | null;
  // Individual income source values for comparison
  attested_income?: number | null;
  deposited_income?: number | null;
  verified_income?: number | null;
  income_confidence?: number | null;

  // Historical scores
  six_month_score: number;
  twelve_month_score: number;
  twenty_four_month_score: number;

  // Audit traceability
  run_id?: string; // Links to algo_runs table for score calculation audit

  // BUG-DATA-001/002: Payment history and DTI ratio (Jan 26, 2026)
  // From lender_member_summary view via algo_run_evidence LATERAL JOINs
  number_of_late_payments?: number | null;
  age_of_late_payments?: number | null;
  dti_ratio_value?: number | null;

  // Optional persona fields. Carried through from API when present and
  // additionally filled by the Demo2 persona overlay (demo2PersonaOverlay.ts)
  // for the 20 Demo CU members whose `members` row data is redacted upstream.
  member_name?: string;
  photo?: string | null;
  age?: number | null;
  inst_loan_advisor?: string | null;
  inst_member_since?: string | null;
  dashboard_status?: string | null;

  // Credit score. Real value when the upstream `credit_scores` table has a
  // row for this member; Demo2 persona overlay sets a synthetic value
  // (`credit_score_source = 'DEMO_SYNTHETIC'`) so the lender UI can show a
  // unified, deterministic number instead of "Not Pulled" / a per-component
  // mock. UI consumers MUST prefer this field over any local mock.
  credit_score?: number | null;
  credit_score_source?: 'credit_scores.score' | 'DEMO_SYNTHETIC' | null;
}

export interface LenderApiError {
  message: string;
  statusCode: number;
}

// ============================================================================
// API Functions
// ============================================================================

// Simple window-level cache for members data
// This persists across component remounts and potential module duplicates
interface GlobalMembersCache {
  members: LenderMember[] | null;
  timestamp: number;
}

declare global {
  interface Window {
    __lenderApiMembersCache?: GlobalMembersCache;
  }
}

const MEMBERS_CACHE_TTL = 30000; // 30 seconds cache

function getGlobalCache(): GlobalMembersCache {
  if (typeof window !== 'undefined') {
    if (!window.__lenderApiMembersCache) {
      window.__lenderApiMembersCache = {
        members: null,
        timestamp: 0
      };
    }
    return window.__lenderApiMembersCache;
  }
  return { members: null, timestamp: 0 };
}

/**
 * Clear the members cache - useful for debugging
 * Call this from browser console: window.clearLenderMembersCache()
 */
export function clearMembersCache(): void {
  const cache = getGlobalCache();
  cache.members = null;
  cache.timestamp = 0;
  console.log('🗑️ [lenderApiService] Members cache cleared');
}

// Expose cache clearing function globally for debugging
if (typeof window !== 'undefined') {
  (window as any).clearLenderMembersCache = clearMembersCache;
}

/**
 * Fetch all members who have shared data with the lender's organization
 * Auth is handled via session cookie (credentials: 'include')
 *
 * SIMPLIFIED: Direct async function without IIFE wrapper to avoid Promise resolution issues
 *
 * @param purpose - PRIV-20.3: Optional explicit purpose for audit trail (defaults to 'lender_review')
 */
export async function getMembers(purpose: LenderPurpose = DEFAULT_PURPOSE): Promise<LenderMember[]> {
  const callId = Math.random().toString(36).substr(2, 6);
  const cache = getGlobalCache();
  const now = Date.now();

  console.log(`📡 [lenderApiService][${callId}] getMembers called`);
  console.log(`📡 [lenderApiService][${callId}] Cache:`, {
    hasMembers: !!cache.members,
    count: cache.members?.length ?? 0,
    age: cache.timestamp ? now - cache.timestamp : 'N/A'
  });

  // Return cached data if fresh
  if (cache.members && cache.members.length > 0 && (now - cache.timestamp) < MEMBERS_CACHE_TTL) {
    console.log(`✅ [lenderApiService][${callId}] Returning ${cache.members.length} cached members (age: ${now - cache.timestamp}ms)`);
    return cache.members;
  }

  // Make the API call directly (no IIFE wrapper)
  console.log(`📡 [lenderApiService][${callId}] Fetching from ${API_BASE}/verascore/lender/members`);

  try {
    const response = await fetch(`${API_BASE}/verascore/lender/members`, {
      method: 'GET',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        'Content-Type': 'application/json',
        [PURPOSE_HEADER]: purpose, // PRIV-20.3: Explicit purpose header (configurable)
      },
      credentials: 'include',
    });

    console.log(`📡 [lenderApiService][${callId}] Response: ${response.status} ${response.ok ? 'OK' : 'FAIL'}`);

    // Handle auth errors - redirect to logout
    if (response.status === 401 || response.status === 403) {
      console.warn(`⚠️ [lenderApiService][${callId}] Session expired (${response.status}), redirecting to logout...`);
      // Clear any cached data
      cache.members = null;
      cache.timestamp = 0;
      // Redirect to logout page
      window.history.replaceState({}, '', '?view=logout');
      window.dispatchEvent(new PopStateEvent('popstate'));
      return [];
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ [lenderApiService][${callId}] API error ${response.status}: ${errorText}`);
      // Return cached data on error if available
      if (cache.members && cache.members.length > 0) {
        console.warn(`⚠️ [lenderApiService][${callId}] Returning ${cache.members.length} stale cached members due to error`);
        return cache.members;
      }
      return [];
    }

    const data = await response.json();
    const rawResult: LenderMember[] = data?.members || data?.data?.members || [];
    // Demo2-only persona overlay: restore canonical demo names + display-only
    // income for redacted Demo CU members. No-op for other orgs.
    const result: LenderMember[] = applyDemo2PersonaOverlayList(rawResult);

    console.log(`✅ [lenderApiService][${callId}] Fetched ${result.length} members from API`);

    // DEBUG: Check if run_id is in API response
    const firstMember = result[0];
    if (firstMember) {
      console.log(`🔍 [lenderApiService][${callId}] FIRST MEMBER run_id check:`, {
        name: firstMember.member_name,
        run_id: firstMember.run_id || 'MISSING',
        has_run_id: 'run_id' in firstMember,
      });
    }

    // Debug: Log Patricia Brown's scores
    const patriciaBrown = result.find(m => m.member_name === 'Patricia Brown');
    if (patriciaBrown) {
      console.log(`🔍 [lenderApiService][${callId}] Patricia Brown API data:`, {
        verascore: patriciaBrown.verascore,
        six_month_score: patriciaBrown.six_month_score,
        twelve_month_score: patriciaBrown.twelve_month_score,
        twenty_four_month_score: patriciaBrown.twenty_four_month_score,
        run_id: patriciaBrown.run_id || 'MISSING',
      });
    }

    // Cache the result
    if (result.length > 0) {
      cache.members = result;
      cache.timestamp = Date.now();
      console.log(`📡 [lenderApiService][${callId}] Cached ${result.length} members`);
    }

    return result;

  } catch (error) {
    console.error(`❌ [lenderApiService][${callId}] Fetch error:`, error);

    // Return cached data on network error if available
    if (cache.members && cache.members.length > 0) {
      console.warn(`⚠️ [lenderApiService][${callId}] Returning ${cache.members.length} stale cached members due to network error`);
      return cache.members;
    }

    // Return empty array instead of throwing (defensive)
    return [];
  }
}

/**
 * Fetch detailed information for a specific member
 * Auth is handled via session cookie (credentials: 'include')
 *
 * @param memberId - Member UUID
 * @param purpose - PRIV-20.3: Optional explicit purpose for audit trail (defaults to 'lender_review')
 */
export async function getMemberDetail(memberId: string, purpose: LenderPurpose = DEFAULT_PURPOSE): Promise<LenderMember> {
  console.log(`📡 [lenderApiService] Fetching member detail: ${memberId}, purpose: ${purpose}`);

  const response = await fetch(`${API_BASE}/verascore/lender/members/${memberId}`, {
    method: 'GET',
    headers: {
      'VS-Client-Id': VS_CLIENT_ID,
      'Content-Type': 'application/json',
      [PURPOSE_HEADER]: purpose, // PRIV-20.3: Explicit purpose header (configurable)
    },
    credentials: 'include', // Sends session cookie for auth
  });

  // Handle auth errors - redirect to logout
  if (response.status === 401 || response.status === 403) {
    console.warn(`⚠️ [lenderApiService] Session expired (${response.status}), redirecting to logout...`);
    // Redirect to logout page
    window.history.replaceState({}, '', '?view=logout');
    window.dispatchEvent(new PopStateEvent('popstate'));
    throw new Error(`Session expired (${response.status})`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ [lenderApiService] API error:', response.status, errorText);
    throw new Error(`API error ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  // Demo2-only persona overlay (single-member detail path).
  const overlaid = applyDemo2PersonaOverlay(data as LenderMember);
  console.log(`✅ [lenderApiService] Fetched member: ${overlaid.first_name} ${overlaid.last_name}`);

  return overlaid;
}

/**
 * Financial inputs for Score Details expanded view
 */
export interface MemberHistoryInputs {
  yearly_gross_income: number | null;
  gross_income: number | null;
  net_savings: number | null;
  liquid_reserves: number | null;
  monthly_expenses: number | null;
  minimum_debt_payments: number | null;
  money_leftover: number | null;
  outstanding_debt: number | null;
  dti_ratio: number | null;
}

/**
 * Historical score data point
 */
export interface MemberHistoryPoint {
  month: string;
  score: number;
  rating: string;
  factors?: {
    cash_flow: number | null;
    debt_to_income: number | null;
    debt: number | null;
    savings: number | null;
    liquid_reserves: number | null;
    payment_history: number | null;
  };
  inputs?: MemberHistoryInputs;
}

/**
 * API response for member history
 */
export interface MemberHistoryResponse {
  memberId: string;
  months: number;
  series: MemberHistoryPoint[];
}

/**
 * Fetch historical VeraScore data for a member
 * Auth is handled via session cookie (credentials: 'include')
 *
 * @param memberId - Member UUID
 * @param options - Query options (months, includeFactors, includeInputs, purpose)
 */
export async function getMemberHistory(
  memberId: string,
  options: { months?: number; includeFactors?: boolean; includeInputs?: boolean; purpose?: LenderPurpose } = {}
): Promise<MemberHistoryResponse> {
  const { months = 24, includeFactors = false, includeInputs = false, purpose = DEFAULT_PURPOSE } = options;

  console.log(`📡 [lenderApiService] Fetching member history: ${memberId}, months=${months}, includeInputs=${includeInputs}, purpose=${purpose}`);

  // Build URL string directly (same pattern as getMembers) to ensure we go through the /api proxy
  const queryParams = new URLSearchParams();
  queryParams.set('months', months.toString());
  if (includeFactors) queryParams.set('includeFactors', 'true');
  if (includeInputs) queryParams.set('includeInputs', 'true');

  const url = `${API_BASE}/verascore/lender/members/${memberId}/history?${queryParams.toString()}`;
  console.log(`📡 [lenderApiService] History URL: ${url}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'VS-Client-Id': VS_CLIENT_ID,
      'Content-Type': 'application/json',
      [PURPOSE_HEADER]: purpose, // PRIV-20.3: Explicit purpose header (configurable)
    },
    credentials: 'include',
  });

  // Handle auth errors - redirect to logout
  if (response.status === 401 || response.status === 403) {
    console.warn(`⚠️ [lenderApiService] Session expired (${response.status}), redirecting to logout...`);
    // Redirect to logout page
    window.history.replaceState({}, '', '?view=logout');
    window.dispatchEvent(new PopStateEvent('popstate'));
    throw new Error(`Session expired (${response.status})`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ [lenderApiService] API error:', response.status, errorText);
    throw new Error(`API error ${response.status}: ${errorText}`);
  }

  const json = await response.json();
  // API returns { status: "success", data: { memberId, months, series } }
  // Extract the inner data object
  const data = json.data || json;
  console.log(`✅ [lenderApiService] Fetched ${data.series?.length || 0} months of history`);

  return data;
}

/**
 * Helper to check if an error is an API error
 */
export function isApiError(error: unknown): error is LenderApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'statusCode' in error
  );
}

// ============================================================================
// Access Event Logging (Audit Trail)
// Epic 16 Sub-Phase 1E: UI Instrumentation for Audit Framework
// ============================================================================

export type AccessEventType = 'VIEW_SCORE' | 'VIEW_FULL_PROFILE' | 'VIEW_AUDIT_PACK' | 'DOWNLOAD_REPORT';

export interface AccessEventRequest {
  member_id: string;
  access_type: AccessEventType;
  run_id?: string;
  fields_accessed?: string[];
  artifact_id?: string;
}

export interface AccessEventResponse {
  success: boolean;
  access_event_id: string;
  logged_at: string;
}

/**
 * Log a lender access event for audit trail
 * Called when lender views member data, expands score details, or downloads reports
 *
 * @param event - Access event details
 * @param purpose - PRIV-20.3: Optional explicit purpose for audit trail (defaults to 'lender_review')
 * @returns Promise<AccessEventResponse> - Logged event details
 */
export async function logAccessEvent(event: AccessEventRequest, purpose: LenderPurpose = DEFAULT_PURPOSE): Promise<AccessEventResponse | null> {
  console.log(`📝 [lenderApiService] Logging access event:`, event, `purpose: ${purpose}`);

  try {
    const response = await fetch(`${API_BASE}/verascore/lender/access-events`, {
      method: 'POST',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        'Content-Type': 'application/json',
        [PURPOSE_HEADER]: purpose, // PRIV-20.3: Explicit purpose header (configurable)
      },
      credentials: 'include',
      body: JSON.stringify(event),
    });

    // Don't redirect on auth errors for logging - just fail silently
    if (!response.ok) {
      console.warn(`⚠️ [lenderApiService] Failed to log access event: ${response.status}`);
      return null;
    }

    const data = await response.json();
    const result = data?.data || data;
    console.log(`✅ [lenderApiService] Access event logged: ${result.access_event_id}`);
    return result;
  } catch (error) {
    // Fail silently - don't break the UI for audit logging failures
    console.warn(`⚠️ [lenderApiService] Error logging access event:`, error);
    return null;
  }
}

// ============================================================================
// Mapper Functions (Convert API response to app types)
// ============================================================================

import type { Member } from '../types/member';
import type { FinancialHealthStats } from '../types/financialStats';

/**
 * Determine the appropriate DashboardStatus based on member data.
 * Maps consent_status and score data to UI-friendly status values.
 */
function determineDashboardStatus(lm: LenderMember): string {
  // If consent not active, show appropriate status
  if (!lm.consent_status || lm.consent_status.toLowerCase() !== 'active') {
    const status = (lm.consent_status || '').toLowerCase();
    if (status === 'pending') return 'Pending';
    if (status === 'invited') return 'Invited';
    if (status === 'revoked') return 'Needs Attention';
    if (status === 'in_progress' || status === 'in progress') return 'In Progress';
    return 'Awaiting Dashboard';
  }

  // If no VeraScore, member is still onboarding
  if (!lm.verascore || lm.verascore === 0) {
    return 'In Progress';
  }

  // If VeraScore is below threshold (40), needs attention
  if (lm.verascore < 40) {
    return 'Needs Attention';
  }

  // Otherwise, dashboard is ready
  return 'Dashboard Ready';
}

/**
 * Convert LenderMember from API to app's Member type
 */
export function mapLenderMemberToMember(lm: LenderMember): Member {
  return {
    uuid: lm.member_id || `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    member_name: (lm.first_name || lm.last_name)
      ? `${lm.first_name} ${lm.last_name}`.trim()
      : lm.member_name || '',
    first_name: lm.first_name || '',
    last_name: lm.last_name || '',
    email: lm.email || '',
    photo: lm.photo ?? null,
    zipcode: lm.zipcode || '',
    gender: '',
    ethnicity: '',
    age: lm.age ?? 0,
    date_of_birth: '',
    street_address: lm.street_address || '',
    apartment: null,
    city: lm.city || '',
    state: lm.state || '',
    yearly_gross_income: lm.yearly_gross_income || 0,
    inst_member_since: lm.inst_member_since ?? '',
    dashboard_status: lm.dashboard_status ?? determineDashboardStatus(lm),
    inst_loan_advisor: lm.inst_loan_advisor ?? '',
    phone_number: lm.phone_number || '',
    run_id: lm.run_id, // Audit traceability
    credit_score: lm.credit_score ?? null,
    credit_score_source: lm.credit_score_source ?? null,
  };
}

/**
 * Convert LenderMember financial data to FinancialHealthStats
 *
 * Note: score_month should always be present. If missing, logs a warning
 * but falls back to current month to avoid breaking the UI.
 * The generateHistoricalFinancialData() function should filter out
 * members without score_month before calling this function.
 */
export function mapLenderMemberToFinancialStats(lm: LenderMember): FinancialHealthStats {
  // Log warning if score_month is missing (should be caught earlier)
  if (!lm.score_month) {
    console.warn(`⚠️ [mapLenderMemberToFinancialStats] Missing score_month for member ${lm.member_name || lm.member_id}, falling back to current month`);
  }

  return {
    month: lm.score_month || new Date().toISOString().slice(0, 7),
    verascore: lm.verascore,
    verascore_health: lm.verascore_rating,
    factor_dti: lm.factor_dti,
    factor_cash_on_hand: lm.factor_liquid_reserves,
    factor_savings: lm.factor_savings,
    factor_spending: lm.factor_cash_flow,
    factor_debt: lm.factor_debt,
    factor_payment_history: lm.factor_payment_history,
    yearly_gross_income: lm.yearly_gross_income,
    gross_income: lm.monthly_gross_income,
  };
}

/**
 * Generate minimal financial data from a LenderMember for initial display
 * Returns only current month data from the summary API
 *
 * NOTE: This does NOT include input fields (money_leftover, dti_ratio, etc.)
 * Those are only available via getMemberHistory() API with includeInputs=true
 *
 * For charts that need input fields, MemberCard will lazy-load complete history
 * when the card is expanded.
 *
 * BUG FIX: Only generate financial data if score_month is present.
 * If score_month is missing, return empty array to avoid creating entries
 * with the current calendar month (which may not have actual data).
 */
export function generateHistoricalFinancialData(lm: LenderMember): FinancialHealthStats[] {
  // Return only current month data - no synthetic historical data
  // MemberCard will fetch complete 24-month history when expanded

  // BUG FIX: Don't generate data if score_month is missing
  // This prevents polluting availableMonths with current calendar month
  // when no actual financial data exists for that month
  if (!lm.score_month) {
    console.warn(`⚠️ [generateHistoricalFinancialData] No score_month for member ${lm.member_name || lm.member_id}, skipping financial data generation`);
    return [];
  }

  return [mapLenderMemberToFinancialStats(lm)];
}

/**
 * Convert LenderMember to MemberWithScore (combined type used by UI)
 */
export interface MemberWithScore extends Member {
  currentScore?: number;
  financialData?: FinancialHealthStats[];
  // Historical score averages for trend calculation
  six_month_score?: number;
  twelve_month_score?: number;
  twenty_four_month_score?: number;
  verascore_rating?: string;
  // Audit traceability - inherited from Member but explicitly noted here
  run_id?: string;
  // BUG-UI-062: Income basis metadata for transparency
  income_basis_used?: 'ATTESTED' | 'DEPOSITED_NET' | 'VERIFIED_GROSS' | null;
  attested_income?: number | null;
  deposited_income?: number | null;
  verified_income?: number | null;
  income_confidence?: number | null;
}

export function mapLenderMemberToMemberWithScore(lm: LenderMember): MemberWithScore {
  // DEBUG: Check run_id at mapping entry
  console.log(`🔍 [mapLenderMemberToMemberWithScore] INPUT lm.run_id for ${lm.member_name}: "${lm.run_id || 'MISSING'}"`);

  const member = mapLenderMemberToMember(lm);

  // DEBUG: Check if mapLenderMemberToMember preserved run_id
  console.log(`🔍 [mapLenderMemberToMemberWithScore] AFTER mapLenderMemberToMember, member.run_id: "${member.run_id || 'MISSING'}"`);

  const historicalData = generateHistoricalFinancialData(lm);

  console.log(`📊 [mapLenderMemberToMemberWithScore] Generated ${historicalData.length} months of data for ${lm.member_name}`);
  console.log(`   Scores: ${historicalData.map(d => `${d.month}=${d.verascore}`).join(', ')}`);

  // Debug: Log the raw verascore value
  if (lm.member_name && lm.member_name.includes('Patricia Brown')) {
    console.log(`🔍 [mapLenderMemberToMemberWithScore] Patricia Brown lm.verascore:`, {
      value: lm.verascore,
      type: typeof lm.verascore,
      stringified: JSON.stringify(lm.verascore),
    });
  }

  const result = {
    ...member,
    currentScore: lm.verascore,
    verascore: lm.verascore, // Add verascore field for MemberCard component
    financialData: historicalData,
    six_month_score: lm.six_month_score,
    twelve_month_score: lm.twelve_month_score,
    twenty_four_month_score: lm.twenty_four_month_score,
    verascore_rating: lm.verascore_rating,
    run_id: lm.run_id, // Audit traceability
    // BUG-UI-062: Income basis metadata for transparency
    income_basis_used: lm.income_basis_used,
    attested_income: lm.attested_income,
    deposited_income: lm.deposited_income,
    verified_income: lm.verified_income,
    income_confidence: lm.income_confidence,
    // BUG-DATA-001/002: Payment history and DTI ratio (Jan 26, 2026)
    number_of_late_payments: lm.number_of_late_payments,
    age_of_late_payments: lm.age_of_late_payments,
    dti_ratio_value: lm.dti_ratio_value,
    credit_score: lm.credit_score ?? null,
    credit_score_source: lm.credit_score_source ?? null,
  };

  // Debug: Log the result currentScore
  if (lm.member_name && lm.member_name.includes('Patricia Brown')) {
    console.log(`🔍 [mapLenderMemberToMemberWithScore] Patricia Brown result.currentScore:`, {
      value: result.currentScore,
      type: typeof result.currentScore,
      stringified: JSON.stringify(result.currentScore),
    });
  }

  return result;
}

// ============================================================================
// Metric Registry (Canonical Labels)
// Epic 16 Sub-Phase 1E: UI Instrumentation for Audit Framework
// ============================================================================

export interface MetricRegistryEntity {
  entity_name: string;
  entity_type: 'METRIC' | 'FACTOR' | 'FEATURE';
  display_name: string;
  description: string;
  formula: string | null;
  period: string | null;
  unit: string | null;
  evidence_level: string;
  ui_label: string | null;
  ui_section: string | null;
}

export interface MetricRegistryResponse {
  factors: MetricRegistryEntity[];
  metrics: MetricRegistryEntity[];
  features: MetricRegistryEntity[];
  _meta: {
    total: number;
    fetched_at: string;
  };
}

/**
 * Fetch metric registry for canonical labels
 * This provides a single source of truth for factor/metric/feature names
 *
 * @param purpose - PRIV-20.3: Optional explicit purpose for audit trail (defaults to 'lender_review')
 * @returns Promise<MetricRegistryResponse | null>
 */
export async function getMetricRegistry(purpose: LenderPurpose = DEFAULT_PURPOSE): Promise<MetricRegistryResponse | null> {
  console.log(`📚 [lenderApiService] Fetching metric registry, purpose: ${purpose}`);

  try {
    const response = await fetch(`${API_BASE}/verascore/metric-registry`, {
      method: 'GET',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        'Content-Type': 'application/json',
        [PURPOSE_HEADER]: purpose, // PRIV-20.3: Explicit purpose header (configurable)
      },
      credentials: 'include',
    });

    if (!response.ok) {
      console.warn(`⚠️ [lenderApiService] Failed to fetch metric registry: ${response.status}`);
      return null;
    }

    const data = await response.json();
    const result = data?.data || data;
    console.log(`✅ [lenderApiService] Fetched metric registry: ${result._meta?.total} entities`);
    return result;
  } catch (error) {
    console.warn(`⚠️ [lenderApiService] Error fetching metric registry:`, error);
    return null;
  }
}

// ============================================================================
// Submission History (IMP-LCU-003)
// ============================================================================

/**
 * Submission history record from member_org_submissions table
 * IMP-LCU-003: For displaying in Evidence Modal Sources tab
 * IMP-LCU-003-v2: Added consentScopes, removed ipAddress/method from display
 */
export interface SubmissionHistoryItem {
  submissionId: string;
  submittedAt: string;
  verascore: number | null;
  yearAndMonth: string | null;
  consentScopes: string[] | null;
  // Kept for API compatibility but not displayed
  method: string;
  ipAddress: string | null;
}

/**
 * Submission history response from API
 */
export interface SubmissionHistoryResponse {
  memberId: string;
  orgId: string;
  totalSubmissions: number;
  submissions: SubmissionHistoryItem[];
}

/**
 * Fetch submission history for a member
 * IMP-LCU-003: Used by Evidence Modal Sources tab to show submission audit trail
 *
 * @param memberId - Member UUID
 * @param limit - Number of submissions to return (default: 3)
 * @param purpose - PRIV-20.3: Optional explicit purpose for audit trail (defaults to 'lender_review')
 * @returns Submission history or null if not found
 */
export async function getSubmissionHistory(
  memberId: string,
  limit: number = 3,
  purpose: LenderPurpose = DEFAULT_PURPOSE
): Promise<SubmissionHistoryResponse | null> {
  console.log(`🔍 [lenderApiService] Fetching submission history for member ${memberId}, purpose: ${purpose}`);

  try {
    // Use template string (not new URL) since API_BASE may be relative path like '/api'
    const url = `${API_BASE}/verascore/lender/members/${memberId}/submission-history?limit=${limit}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        [PURPOSE_HEADER]: purpose, // PRIV-20.3: Explicit purpose header (configurable)
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log(`📭 [lenderApiService] No submission history for member ${memberId}`);
        return null;
      }
      console.warn(`⚠️ [lenderApiService] Failed to fetch submission history: ${response.status}`);
      return null;
    }

    const data = await response.json();
    const result = data?.data || data;
    console.log(`✅ [lenderApiService] Fetched ${result.submissions?.length || 0} submissions (total: ${result.totalSubmissions})`);
    return result;
  } catch (error) {
    console.warn(`⚠️ [lenderApiService] Error fetching submission history:`, error);
    return null;
  }
}

